import { useEffect, useId, useSyncExternalStore } from 'react';

/* Éditeur (cellule, titre de colonne) auquel s'adresse la section « Insertion »
   du panneau de droite. Cette section est unique et portée par le tableau (cf.
   TableInsertionPanel) : chaque cellule ou en-tête y déclare seulement son
   éditeur quand il est sélectionné. Quand chacun montait sa propre section, elle
   était retirée à la désélection d'une cellule puis remontée à la sélection de
   la suivante, avec un intervalle où elle disparaissait (clic sur la cellule
   suivante -> sauvegarde, rendu du tableau, focus...).

   Un petit magasin externe plutôt qu'un état du tableau : `editorState` change à
   chaque frappe, et ne doit faire rendre que la section, pas le tableau. */
let target = null;
const listeners = new Set();

const publish = (next) => {
  target = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useInsertionTarget = () => useSyncExternalStore(subscribe, () => target);

// Table désélectionnée (ou cellule sans section, ex. une image) : plus de cible.
export const clearInsertionTarget = (boxId) => {
  if (target && (boxId === undefined || target.boxId === boxId)) publish(null);
};

/* Déclare l'éditeur d'une cellule/en-tête. Désélectionné, il garde sa place tant
   qu'aucun autre ne la prend (la section ne clignote pas entre deux cellules),
   mais devient inactif : la section n'insère alors plus rien dedans (cf.
   TableInsertionPanel), son contenu n'étant plus sauvegardé. */
export const useInsertionTargetRegistration = ({
  boxId,
  isSelected,
  wantsPanel = true,
  editorState,
  setEditorState,
  setDefaultSelection,
  hasImage = true,
}) => {
  const ownerId = useId();

  useEffect(() => {
    if (isSelected && wantsPanel) {
      publish({ boxId, ownerId, active: true, editorState, setEditorState, setDefaultSelection, hasImage });
    } else if (isSelected) {
      clearInsertionTarget(boxId);
    } else if (target?.ownerId === ownerId && target.active) {
      publish({ ...target, active: false });
    }
  }, [boxId, ownerId, isSelected, wantsPanel, editorState, setEditorState, setDefaultSelection, hasImage]);

  useEffect(() => () => {
    if (target?.ownerId === ownerId) publish(null);
  }, [ownerId]);
};
